/* Black Friday offers: Shopify is the source of truth for Applied state. */
(() => {
  if (window.BFOfferElement) return;
  let activeOperation = null;
  const normalize = (code) => String(code || '').trim().toLowerCase();
  const applications = (cart) => [
    ...(cart.cart_level_discount_applications || []),
    ...(cart.items || []).flatMap((item) => (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application))
  ].filter((application) => application?.type === 'discount_code');
  const activeCodes = (cart) => [...new Set([
    ...(cart.discount_codes || []).filter((discount) => discount.applicable === true).map((discount) => discount.code),
    ...applications(cart).map((application) => application.title)
  ].filter(Boolean))];
  const isApplied = (cart, code) => {
    const reported = cart.discount_codes?.find((discount) => normalize(discount.code) === normalize(code));
    if (reported) return reported.applicable === true;
    return Boolean(code) && activeCodes(cart).some((value) => normalize(value) === normalize(code));
  };

  class BFOfferElement extends HTMLElement {
    connectedCallback() {
      this.addRequest?.abort();
      this.addRequest = null;
      this.querySelectorAll('[data-add-form] button').forEach((button) => button.removeAttribute('aria-busy'));
      this.request?.abort();
      this.request = null;
      this.lifecycle = new AbortController();
      const { signal } = this.lifecycle;
      this.buttons = Array.from(this.querySelectorAll('[data-apply]'));
      this.button = this.buttons[0];
      this.notice = this.querySelector('[data-notice]');
      this.pending = false;
      this.applied = false;
      this.revision = 0;
      this.buttons.forEach((button) => { button.disabled = false; });
      this.renderButton();
      this.classList.add('is-enhanced');
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('submit', (event) => {
        if (event.target.matches('[data-add-form]')) { event.preventDefault(); this.add(event.target); }
      }, { signal });
      // Native quick-buy modals move to <body> when opened. Capture their form
      // before the theme submits so the selected variant is added exactly once.
      document.addEventListener('submit', (event) => {
        const modal = event.target.closest('quick-buy-modal[data-auto-discount]');
        if (!this.dataset.bfOfferOwner || modal?.dataset.bfOfferOwner !== this.dataset.bfOfferOwner ||
          !event.target.matches('form') || !event.target.closest('product-form') || !event.target.elements.id) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.add(event.target, { autoDiscount: true, modal });
      }, { signal, capture: true });
      document.addEventListener('cart:change', (event) => {
        this.revision++;
        if (event.detail?.cart) this.sync(event.detail.cart);
      }, { signal });
      document.addEventListener('cart:refresh', () => this.refresh(), { signal });
      window.addEventListener('pageshow', (event) => { if (event.persisted) this.refresh(); }, { signal });
      this.notice.addEventListener('mouseenter', () => clearTimeout(this.noticeTimer), { signal });
      this.notice.addEventListener('focusin', () => clearTimeout(this.noticeTimer), { signal });
      this.notice.addEventListener('mouseleave', () => this.scheduleDismiss(), { signal });
      this.notice.addEventListener('focusout', () => this.scheduleDismiss(), { signal });
      document.addEventListener('bf:offer:busy', () => this.renderButton(), { signal });
      document.addEventListener('bf:notice', (event) => { if (event.detail.owner !== this) this.notice.hidden = true; }, { signal });
      document.addEventListener('shopify:section:load', () => { this.applied = false; this.renderButton(); this.refresh(); }, { signal });
      document.addEventListener('shopify:section:unload', () => {
        queueMicrotask(() => { if (this.isConnected) { this.applied = false; this.renderButton(); this.refresh(); } });
      }, { signal });
      this.refresh();
    }

    get code() {
      return this.dataset.code?.trim() || (this.dataset.inheritCode === 'true' ? document.querySelector('bf-savings')?.dataset.code?.trim() || '' : '');
    }

    acquire(kind) {
      if (activeOperation) return false;
      activeOperation = { owner: this, kind, code: this.code };
      document.dispatchEvent(new CustomEvent('bf:offer:busy'));
      return true;
    }

    release() {
      if (activeOperation?.owner !== this) return;
      activeOperation = null;
      document.dispatchEvent(new CustomEvent('bf:offer:busy'));
    }

    disconnectedCallback() {
      this.addRequest?.abort();
      this.addRequest = null;
      this.lifecycle?.abort();
      this.request?.abort();
      this.request = null;
      this.release();
      clearTimeout(this.noticeTimer);
      this.querySelector('dialog[open]')?.close();
    }

    root() { return window.Shopify?.routes?.root || '/'; }
    message(key) { return this.dataset[key] || ''; }

    async json(url, options = {}) {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error('Cart request failed');
      if (!Array.isArray(data.items) || typeof data.item_count !== 'number') throw new Error('Invalid cart response');
      return data;
    }

    async refresh() {
      if (!this.buttons?.length || !this.code || this.pending) return;
      const revision = ++this.revision;
      try {
        const cart = await this.json(`${this.root()}cart.js`, { signal: this.lifecycle.signal });
        if (this.isConnected && this.revision === revision && !this.pending) this.sync(cart);
      } catch { /* A failed background read must never invent Applied state. */ }
    }

    sync(cart) {
      this.applied = isApplied(cart, this.code);
      this.renderButton();
    }

    renderButton() {
      const loading = this.pending || (activeOperation?.kind === 'discount' && normalize(activeOperation.code) === normalize(this.code));
      for (const button of this.buttons || []) {
        const label = this.message(loading ? 'loadingLabel' : this.applied ? 'appliedLabel' : 'applyLabel');
        button.querySelector('[data-label]').textContent = label;
        if (button.dataset.applyContext) button.setAttribute('aria-label', `${label} — ${button.dataset.applyContext}`);
        button.setAttribute('aria-busy', String(loading));
        button.setAttribute('aria-disabled', String(Boolean(activeOperation) || this.applied || !this.code));
        button.classList.toggle('is-applied', this.applied && !loading);
        if (button.hasAttribute('data-coupon-control')) {
          button.hidden = !this.code;
          button.setAttribute('aria-checked', String(this.applied));
          const price = button.closest('[data-card-price]');
          price.querySelector('[data-regular-price]').hidden = true;
          price.querySelector('[data-coupon-price]').hidden = false;
          price.querySelector('[data-coupon-badge]')?.removeAttribute('hidden');
        }
      }
    }

    async apply() {
      if (this.pending || activeOperation) return;
      if (!this.code) { this.notify(this.message('unavailableMessage'), 'info'); return; }
      if (!this.acquire('discount')) return;
      this.pending = true;
      this.revision++;
      this.renderButton();
      const request = new AbortController();
      const lifecycle = this.lifecycle;
      const isCurrent = () => this.isConnected && this.lifecycle === lifecycle;
      this.request = request;
      const timeout = setTimeout(() => request.abort(), 15000);
      let submitted = false;
      try {
        // Read immediately before a mutation to preserve the customer's existing codes.
        const before = await this.json(`${this.root()}cart.js`, { signal: request.signal });
        if (!isCurrent()) return;
        if (isApplied(before, this.code)) {
          this.sync(before);
          this.notify(this.message('successMessage'), 'success');
          return;
        }
        this.sync(before);
        if (!before.item_count) { this.notify(this.message('emptyMessage'), 'info'); return; }
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        const requestedSections = [...new Set(sections)].slice(0, 5);
        const codes = [...activeCodes(before).filter((value) => normalize(value) !== normalize(this.code)), this.code];
        submitted = true;
        const cart = await this.json(`${this.root()}cart/update.js`, {
          method: 'POST', signal: request.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(','), sections: requestedSections, sections_url: window.location.pathname })
        });
        if (!isCurrent()) return;
        this.sync(cart);
        // Prestige expects rendered drawer HTML on cart:change. A null section
        // is a rendering failure, not a failed discount; use its refresh path.
        if (requestedSections.every((id) => typeof cart.sections?.[id] === 'string' && cart.sections[id].trim())) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true, detail: { baseEvent: 'discount:apply', cart }
          }));
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
        const replaced = activeCodes(before).some((code) => !isApplied(cart, code));
        this.notify(this.message(this.applied ? (replaced ? 'changedMessage' : 'successMessage') : 'invalidMessage'), this.applied ? 'success' : 'error');
      } catch {
        if (isCurrent()) {
          this.applied = false;
          this.notify(this.message('errorMessage'), 'error');
          // A timed-out response may still have changed the server cart.
          if (submitted) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      } finally {
        clearTimeout(timeout);
        if (this.request === request) {
          this.pending = false;
          this.request = null;
          this.release();
          if (this.isConnected) { this.renderButton(); this.refresh(); }
        }
      }
    }

    offerIsExclusive(cart, code) {
      return isApplied(cart, code) && activeCodes(cart).every((value) => normalize(value) === normalize(code));
    }

    async replaceCartOffer(cart, code, signal, sections) {
      if (this.offerIsExclusive(cart, code)) return cart;
      const previousCodes = activeCodes(cart);
      const update = (discount) => this.json(`${this.root()}cart/update.js`, {
        method: 'POST', signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount, sections, sections_url: window.location.pathname })
      });
      let updated = await update(code);
      // A rejected offer must not silently remove a valid previous discount.
      if (!isApplied(updated, code) && previousCodes.length && !signal.aborted) {
        updated = await update(previousCodes.join(','));
      }
      return updated;
    }

    async add(form, { autoDiscount = form.hasAttribute('data-auto-discount'), modal = null } = {}) {
      if (!form.checkValidity()) { form.reportValidity(); return; }
      const button = [...form.elements].find((element) => element.type === 'submit');
      if (!button) { this.notify(this.message('addErrorMessage'), 'error'); return; }
      if (!this.acquire('add')) { this.notify(this.message('busyMessage'), 'info'); return; }
      this.lastTrigger = modal ? [...this.querySelectorAll('[aria-controls]')].find((trigger) => trigger.getAttribute('aria-controls') === modal.id) || button : button;
      button.setAttribute('aria-busy', 'true');
      const request = new AbortController();
      const lifecycle = this.lifecycle;
      const isCurrent = () => this.isConnected && this.lifecycle === lifecycle;
      const code = this.code;
      this.addRequest = request;
      const timeout = setTimeout(() => request.abort(), 25000);
      let accepted = false;
      let attempted = false;
      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        const sectionIds = [...new Set(sections)].slice(0, 5);
        const formData = new FormData(form);
        formData.set('sections', sectionIds.join(','));
        formData.set('sections_url', window.location.pathname);
        attempted = true;
        const response = await fetch(`${this.root()}cart/add.js`, {
          method: 'POST', credentials: 'same-origin', signal: request.signal,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await response.json();
        if (!isCurrent()) return;
        if (!response.ok || data.status >= 400) {
          attempted = false;
          this.notify(typeof data.description === 'string' ? data.description : this.message('addErrorMessage'), 'error');
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          return;
        }
        const items = Array.isArray(data.items) ? data.items : data.id ? [data] : [];
        if (!items.length) throw new Error('Invalid add response');
        accepted = true;
        let cart = await this.json(`${this.root()}cart.js`, { signal: request.signal });
        if (!isCurrent()) return;
        cart.sections = data.sections || {};
        if (autoDiscount && code) {
          try {
            cart = await this.replaceCartOffer(cart, code, request.signal, sectionIds);
          } catch {
            // Never retry the add: only re-read the cart if the discount response
            // was interrupted, since the server may already have accepted it.
            cart = await this.json(`${this.root()}cart.js`, { signal: request.signal });
          }
        }
        if (!isCurrent()) return;
        this.sync(cart);
        const onSuccessDo = formData.get('on_success');
        form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items, cart, onSuccessDo } }));
        if (sectionIds.every((id) => typeof cart.sections?.[id] === 'string' && cart.sections[id].trim())) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart, onSuccessDo } }));
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
        if (autoDiscount) {
          const applied = Boolean(code) && this.offerIsExclusive(cart, code);
          this.notify(this.message(applied ? 'addedDiscountMessage' : 'addedWithoutDiscountMessage'), applied ? 'success' : 'info');
        } else {
          this.notify(this.message('addedMessage'), 'success');
        }
      } catch {
        if (isCurrent()) {
          const message = accepted ? (autoDiscount ? 'addedWithoutDiscountMessage' : 'addedMessage') : 'addUncertainMessage';
          this.notify(this.message(message), accepted && !autoDiscount ? 'success' : accepted ? 'info' : 'error');
          if (attempted) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      } finally {
        clearTimeout(timeout);
        if (this.addRequest === request) {
          this.addRequest = null;
          button.removeAttribute('aria-busy');
          this.release();
        }
      }
    }

    notify(message, kind) {
      document.dispatchEvent(new CustomEvent('bf:notice', { detail: { owner: this } }));
      clearTimeout(this.noticeTimer);
      this.notice.dataset.kind = kind;
      this.notice.hidden = false;
      this.notice.querySelector('[data-notice-text]').textContent = message;
      this.scheduleDismiss();
    }

    scheduleDismiss() {
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => {
        if (!this.notice.matches(':hover') && !this.notice.contains(document.activeElement)) this.notice.hidden = true;
      }, 6000);
    }

    onClick(event) {
      const applyButton = event.target.closest('[data-apply]');
      if (applyButton) {
        if (applyButton.hasAttribute('data-coupon-control') && applyButton.getAttribute('aria-disabled') === 'true') return;
        this.lastTrigger = applyButton; this.apply();
      }
      if (event.target.closest('[data-dismiss]')) {
        this.notice.hidden = true;
        (this.lastTrigger?.isConnected && !this.lastTrigger.closest('[hidden]') ? this.lastTrigger : this.querySelector('[data-search]') || this.button)?.focus({ preventScroll: true });
      }
    }
  }
  window.BFOfferElement = BFOfferElement;
})();
